import { useState, useEffect, useCallback, useRef } from 'react';
import { useAppDispatch } from '@store/hooks';
import {
  loadDraft,
  setEventId,
  setEtag,
  setRequestId,
  setAvailableSpaces,
  setSpacesContinuationToken,
  setSpacesHasMore,
  selectSpace as selectSpaceAction,
  goToStep,
  updateStepData,
} from '@store/slices/bookingFormSlice';
import { eventServiceApi } from '@store/api/eventService/eventServiceApi';
import { spaceConfigServiceApi } from '@store/api/spaceConfigService/spaceConfigServiceApi';
import {
  mapEventDataToFormData,
  buildBookingDraft,
  mapSpaceToDetails,
  getStepIndex,
} from '@utils/eventToFormMapper';
import type { EventId } from '@store/models/ids';
import type { Building } from '@store/models/buildings';

// Page size for the first lazy-load batch on draft resume (spec 103 FR-016 + Decision 10).
const SPACES_PAGE_SIZE = 10;

interface UseDraftResumeResult {
  isLoading: boolean;
  error: string | null;
  building: Building | null;
  buildingLoading: boolean;
  spacesLoading: boolean;
}

export function useDraftResume(eventId: EventId | null): UseDraftResumeResult {
  const dispatch = useAppDispatch();
  const [isLoading, setIsLoading] = useState(!!eventId);
  const [error, setError] = useState<string | null>(null);
  const [building, setBuilding] = useState<Building | null>(null);
  const [buildingLoading, setBuildingLoading] = useState(false);
  const [spacesLoadingState, setSpacesLoadingState] = useState(false);
  const runTokenRef = useRef(0);

  const [triggerGetEvent] = eventServiceApi.useLazyGetEventByIdQuery();
  const [triggerGetBuilding] = spaceConfigServiceApi.useLazyGetBuildingByIdQuery();
  const [triggerGetSpaces] = spaceConfigServiceApi.useLazyGetSpacesQuery();
  const [triggerGetBuildings] = spaceConfigServiceApi.useLazyGetBuildingsQuery();

  const resumeDraft = useCallback(async () => {
    if (!eventId) return;

    // Token identifying this run. If the ref advances (a newer run started or
    // the effect was torn down), this run is stale and must not touch state.
    const token = ++runTokenRef.current;
    const isStale = () => token !== runTokenRef.current;

    setIsLoading(true);
    setError(null);

    try {
      const eventResult = await triggerGetEvent({ eventId }).unwrap();
      // A StrictMode remount (dev) tears down the lazy-query subscription and
      // aborts this request; bail out so the superseded run stays silent.
      if (isStale()) return;
      const event = eventResult.data;

      if (!event || event.eventState !== 'DRAFT') {
        setError('This event is not in a draft state and cannot be resumed.');
        setIsLoading(false);
        return;
      }

      // Fetch building first so we have the timezone for correct time extraction
      let buildingData: Building | null = null;
      if (event.buildingId) {
        setBuildingLoading(true);
        try {
          const buildingResult = await triggerGetBuilding(event.buildingId).unwrap();
          if (buildingResult.data) {
            buildingData = buildingResult.data;
            setBuilding(buildingData);
          }
        } catch {
          // Building fetch failed — form will show buildingId as fallback
        } finally {
          setBuildingLoading(false);
        }
      }

      const buildingTimeZone = buildingData?.gmtZone;
      const formData = mapEventDataToFormData(event, buildingTimeZone);
      const draft = buildBookingDraft(event, formData);

      dispatch(loadDraft(draft));
      dispatch(setEventId(event.eventId));
      if (eventResult.etag) {
        dispatch(setEtag(eventResult.etag));
      }
      if (event.requestId) {
        dispatch(setRequestId(event.requestId as string));
      }

      if (buildingData) {
        dispatch(
          updateStepData({
            stepIndex: 0,
            data: {
              stateProvince: buildingData.stateCode,
              stateName: buildingData.stateName,
              buildingId: buildingData.buildingId,
              buildingName: buildingData.buildingName,
              eventStartDate: event.eventStartDate,
              eventEndDate: event.eventEndDate,
            },
          })
        );

        // Pre-fetch buildings list so dropdowns are
        // populated immediately when useBookingForm renders.
        await triggerGetBuildings()
          .unwrap()
          .catch(() => {
            // Non-critical — useBookingForm will retry via its own query
          });
      }

      if (event.buildingId && event.eventStartDate && event.eventEndDate) {
        setSpacesLoadingState(true);

        const selectedSpaceId = event.spaces?.[0]?.spaceId;

        try {
          const dateRange = formData.eventDateRange as { start?: string; end?: string } | undefined;
          const timeRange = formData.eventTimeRange as { start?: string; end?: string } | undefined;
          const spacesResult = await triggerGetSpaces({
            buildingId: event.buildingId,
            ...(dateRange?.start ? { startDate: dateRange.start } : {}),
            ...(dateRange?.end ? { endDate: dateRange.end } : {}),
            ...(timeRange?.start ? { startTime: timeRange.start } : {}),
            ...(timeRange?.end ? { endTime: timeRange.end } : {}),
            pageSize: SPACES_PAGE_SIZE,
          }).unwrap();

          if (spacesResult.data) {
            dispatch(setAvailableSpaces(spacesResult.data));
            dispatch(
              setSpacesContinuationToken(spacesResult.pagination?.continuationToken ?? null)
            );
            dispatch(setSpacesHasMore(Boolean(spacesResult.pagination?.hasMore)));

            if (selectedSpaceId) {
              const matchedSpace = spacesResult.data.find((s) => s.spaceId === selectedSpaceId);
              if (matchedSpace) {
                const spaceDetails = mapSpaceToDetails(matchedSpace);
                dispatch(selectSpaceAction(spaceDetails));
              }
            }
          }
        } catch {
          // Spaces fetch failed — Review will show fallback
        } finally {
          setSpacesLoadingState(false);
        }
      }

      // Resume on the step persisted on the draft (backend `currentStep`).
      // Legacy fallback (only when currentStep is missing): if a space exists, assume past step 1.
      let resolvedStep = getStepIndex(event.currentStep);
      if (!event.currentStep && event.spaces?.[0]?.spaceId && resolvedStep < 2) {
        resolvedStep = 2;
      }
      dispatch(goToStep({ stepIndex: resolvedStep }));
    } catch {
      // Ignore failures from a superseded/aborted run (e.g. StrictMode's
      // dev-only mount/unmount/mount cycle cancels the first request).
      if (isStale()) return;
      setError('Failed to load draft event. It may have been deleted or you may not have access.');
    } finally {
      if (!isStale()) setIsLoading(false);
    }
  }, [
    eventId,
    dispatch,
    triggerGetEvent,
    triggerGetBuilding,
    triggerGetSpaces,
    triggerGetBuildings,
  ]);

  useEffect(() => {
    if (!eventId) return undefined;
    void resumeDraft();
    const runToken = runTokenRef.current;
    return () => {
      // Invalidate the in-flight run so its (possibly aborted) result is
      // ignored and the next mount starts a fresh fetch.
      runTokenRef.current = runToken + 1;
    };
  }, [eventId, resumeDraft]);

  return { isLoading, error, building, buildingLoading, spacesLoading: spacesLoadingState };
}
